/**
 * This file contains utilities for using client hints for user preference which
 * are needed by the server, but are only known by the browser.
 */
import { getHintUtils } from '@epic-web/client-hints'
import {
	clientHint as colorSchemeHint,
	subscribeToSchemeChange,
} from '@epic-web/client-hints/color-scheme'
import { clientHint as timeZoneHint } from '@epic-web/client-hints/time-zone'
import * as React from 'react'
import { useRevalidator } from 'react-router'
import { useOptionalRequestInfo, useRequestInfo } from './request-info.ts'

// Custom client hint for KBase filter mode
const kbaseFilterModeHint = {
	cookieName: 'CH-kbase-filter-mode',
	getValueCode: `window.localStorage.getItem('kbaseFilterMode') || 'my-roles'`,
	fallback: 'my-roles' as 'mine' | 'shared' | 'my-roles' | 'all',
	transform(value: string): 'mine' | 'shared' | 'my-roles' | 'all' {
		if (value === 'mine' || value === 'shared' || value === 'my-roles' || value === 'all') {
			return value
		}
		return 'my-roles'
	},
}

// Custom client hint for KBase view mode
const kbaseViewModeHint = {
	cookieName: 'CH-kbase-view-mode',
	getValueCode: `window.localStorage.getItem('kbaseViewMode') || 'list'`,
	fallback: 'list',
	transform(value: string) {
		return value === 'grid' ? 'grid' : 'list'
	},
}

// Custom client hint for KBase selected role filters (admin only)
const kbaseSelectedRolesHint = {
	cookieName: 'CH-kbase-selected-roles',
	getValueCode: `window.localStorage.getItem('kbaseSelectedRoles') || '[]'`,
	fallback: '[]',
	transform(value: string) {
		try {
			const parsed = JSON.parse(value)
			return Array.isArray(parsed) ? parsed : []
		} catch {
			return []
		}
	},
}

const hintsUtils = getHintUtils({
	theme: colorSchemeHint,
	timeZone: timeZoneHint,
	kbaseFilterMode: kbaseFilterModeHint,
	kbaseViewMode: kbaseViewModeHint,
	kbaseSelectedRoles: kbaseSelectedRolesHint,
	// add other hints here
})

export const { getHints } = hintsUtils

/**
 * @returns an object with the client hints and their values
 */
export function useHints() {
	const requestInfo = useRequestInfo()
	return requestInfo.hints
}

export function useOptionalHints() {
	const requestInfo = useOptionalRequestInfo()
	return requestInfo?.hints
}

/**
 * @returns inline script element that checks for client hints and sets cookies
 * if they are not set then reloads the page if any cookie was set to an
 * inaccurate value.
 */
export function ClientHintCheck({ nonce }: { nonce: string }) {
	const { revalidate } = useRevalidator()
	React.useEffect(
		() => subscribeToSchemeChange(() => revalidate()),
		[revalidate],
	)

	return (
		<script
			nonce={nonce}
			// suppressHydrationWarning is required for CSP nonces
			// Nonces must be unique per request for security, so they will always
			// differ between server render and client hydration. This is expected
			// behavior and doesn't affect functionality.
			// See: https://react.dev/reference/react-dom/components/script#special-rendering-behavior
			suppressHydrationWarning
			dangerouslySetInnerHTML={{
				__html: hintsUtils.getClientHintCheckScript(),
			}}
		/>
	)
}
